import { Alert, Box, CircularProgress, Typography, useMediaQuery, useTheme } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Helmet } from "react-helmet-async";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { PublishedArticleDetail } from "src/api/articles";
import articleMarkdownStyles from "./articlesDrawer.module.css";
import ThumbsResponse from "../ThumbsResponse/ThumbsResponse";
import { useAuth0 } from "@auth0/auth0-react";
import { useContext, useState } from "react";
import { WidgetContext } from "src/contexts/WidgetContext";
import { Share } from "@capacitor/share";
import { buildArticleDeepLinkUrl } from "src/config/frontendUrls";
import { buildArticleCanonicalUrl, SEO_OG_IMAGE, SEO_SITE_NAME } from "src/config/seo.config";
import { getAppOrigin } from "src/utils/legalPageUrls";

function buildArticleMetaDescription(body: string): string {
  const plain = body
    .replace(/!\[[^\]]*]\([^)]+\)/g, " ")
    .replace(/\[[^\]]*]\([^)]+\)/g, " ")
    .replace(/[#>*_`~]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return "RotoGPT fantasy sports article";
  return plain.length > 160 ? `${plain.slice(0, 157)}...` : plain;
}

export function formatArticleTimestamp(value: string): string {
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour12: false,
    })
      .format(date)
      .replace(",", "");
  }
  return value;
}

export interface ArticlesResponseProps {
  loading: boolean;
  error: string | null;
  article: PublishedArticleDetail | null;
}

/**
 * Article detail body (image, headline, author/date, markdown) — same role as WaiverResponse for waivers.
 */
function ArticlesResponse({ loading, error, article }: ArticlesResponseProps) {
  const { user } = useAuth0();
  const { selectedTeamInfo, NON_LOGGED_IN_CONFIG } = useContext(WidgetContext);
  const authId = user?.sub ?? NON_LOGGED_IN_CONFIG?.NON_LOGGED_IN_AUTH_ID;
  const [isCheckmark, setIsCheckmark] = useState(false);
  // Same mobile detection as useHandleShare (waivers/trades share sheet)
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg")) && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={40} sx={{ color: PureLightThemeCaaS.colors.alpha.orange[100] }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        {error}
      </Alert>
    );
  }

  if (!article) {
    return null;
  }

  // Public /articles/:slug via REACT_APP_FRONTEND_URI_WEB (not capacitor://). Mobile: share sheet; desktop: clipboard.
  const handleShareArticle = async () => {
    try {
      const slugOrId = article.slug?.trim() || article.id;
      const shareUrl = buildArticleDeepLinkUrl(slugOrId, getAppOrigin());
      if (!shareUrl) {
        console.error("Share failed: article URL is not available");
        return;
      }
      if (isMobileOrTablet) {
        await Share.share({
          title: "Check out this RotoGPT article!",
          url: shareUrl,
          dialogTitle: "Share this article",
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
      }
      setIsCheckmark(true);
      setTimeout(() => setIsCheckmark(false), 1500);
    } catch (error) {
      console.error("Failed to share article URL:", error);
    }
  };

  const ts = formatArticleTimestamp(article.date);
  const slugOrId = article.slug?.trim() || article.id;
  const canonicalUrl = buildArticleCanonicalUrl(slugOrId, getAppOrigin());
  const metaDescription = buildArticleMetaDescription(article.body);
  const pageTitle = `${article.headline} | RotoGPT`;
  const ogImage = article.imageUrl || SEO_OG_IMAGE;
  const articleJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.headline,
    description: metaDescription,
    author: { "@type": "Person", name: article.author },
    datePublished: article.date,
    image: ogImage,
    publisher: { "@type": "Organization", name: SEO_SITE_NAME },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
  });
  const articleImageFrameSx = {
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.1)",
    mb: 2,
  } as const;
  const articleImageSx = {
    width: "100%",
    height: "auto",
    display: "block",
    bgcolor: "#111",
  } as const;

  return (
    <Box>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content={SEO_SITE_NAME} />
        <meta property="og:title" content={article.headline} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.headline} />
        <meta name="twitter:description" content={metaDescription} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">{articleJsonLd}</script>
      </Helmet>
      <Box sx={articleImageFrameSx}>
        {/* <Box component="img" src={article.imageUrl} alt="" sx={{ width: "100%", display: "block", maxHeight: 220, objectFit: "cover", bgcolor: "#111" }} /> */}
        <Box component="img" src={article.imageUrl} alt="" sx={articleImageSx} />
      </Box>

      <Typography
        sx={{
          color: "#fff",
          fontWeight: 900,
          fontSize: 22,
          lineHeight: 1.15,
          textTransform: "uppercase",
          mb: 1,
        }}
      >
        {article.headline}
      </Typography>

      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2 }}>
        <Box component="span" sx={{ color: PureLightThemeCaaS.colors.alpha.orange[100] }}>
          By {article.author}
        </Box>
        <Box component="span" sx={{ color: PureLightThemeCaaS.colors.alpha.white[50] }}>
          {" "}
          • {ts}
        </Box>
      </Typography>

      <Typography component="div" sx={{ color: PureLightThemeCaaS.colors.alpha.white[70], fontSize: 14, mb: 1 }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          className={articleMarkdownStyles.ArticlesReactMarkdown}
          components={{
            img: ({ src, alt, title }) => (
              <Box sx={articleImageFrameSx}>
                <Box component="img" src={src ?? ""} alt={alt ?? ""} title={title} sx={articleImageSx} />
              </Box>
            ),
          }}
        >
          {article.body}
        </ReactMarkdown>
      </Typography>

      {article.geoText?.trim() ? (
        <span className={articleMarkdownStyles.ArticleGeoHidden}>{article.geoText.trim()}</span>
      ) : null}

      <ThumbsResponse
        messageId={article.id}
        userId={authId}
        message={"No message, its articles section"}
        response={article.headline}
        section="articles"
        platformId={selectedTeamInfo?.platformId}
        leagueId={selectedTeamInfo?.leagueId}
        teamName={selectedTeamInfo?.teamName}
        handleShare={handleShareArticle}
        isCheckmark={isCheckmark}
      />

      {/* <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: 2,
          border: "1px solid rgba(255, 103, 45, 0.35)",
          bgcolor: "rgba(24, 28, 36, 0.95)",
          display: "flex",
          gap: 1.25,
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1,
            bgcolor: "#070a12",
            border: "1px solid rgba(255,255,255,0.12)",
            color: PureLightThemeCaaS.colors.alpha.orange[100],
            fontSize: 11,
            fontWeight: 900,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          00
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 13, mb: 0.35 }}>Want stats for YOUR team?</Typography>
          <Typography sx={{ color: PureLightThemeCaaS.colors.alpha.white[100], fontSize: 11, lineHeight: 1.45, mb: 1 }}>
            Connect your fantasy roster to see personalized trade analysis.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 800,
              py: 0.5,
              bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
              color: "#000",
              "&:hover": { bgcolor: PureLightThemeCaaS.colors.alpha.orange[80] },
            }}
          >
            Connect Team
          </Button>
        </Box>
      </Paper> */}
    </Box>
  );
}

export default ArticlesResponse;
